import { BRAND_LINKS, SUPPORT_EMAIL, SUPPORT_WHATSAPP_DISPLAY } from "@tami/core";

import {
  PRIVACY_POLICY_DOCUMENT,
  TERMS_OF_USE_DOCUMENT,
} from "@/lib/legal/legal-documents";

import { defineCopy } from "@tami/core/intl";

import { BIO_COPY_EN_US } from "./en-us/bio-site.copy";
import { BIO_COPY_ES_ES } from "./es-es/bio-site.copy";

/**
 * Todo o texto e todos os destinos da `/links` (a página que ocupa a bio do
 * Instagram) num arquivo só.
 *
 * Fica no app, e não no `@tami/core`, porque a tela é só-web: o `CLAUDE.md` do
 * web manda o texto subir quando outra plataforma construir a mesma tela.
 */

/** A `/demo` já existe; os chips de funcionalidade caem direto numa tela dela. */
export const DEMO_BASE_HREF = "/demo";

/**
 * O `utm_source` que o `withAttribution` grava para quem chega pela bio do
 * Instagram — o `buildAttribution` lê este valor em `source`.
 */
export const BIO_UTM_SOURCE = "bio";

export const BIO_COPY_PT_BR = {
  /**
   * A direção aprovada (`docs/design/bio-links/direcao-3-estudio.html`) grifa
   * só o verbo "organiza" com o marca-texto verde — por isso a frase vem
   * partida em três pedaços, e não como uma string só, para o componente
   * desenhar o grifo sem depender de parsing de marcação no meio do texto.
   */
  tagline: {
    before: "A ferramenta que ",
    highlight: "organiza",
    after: " a sua vida de creator.",
  },
  social: [
    { key: "instagram", ...BRAND_LINKS.instagram },
    { key: "youtube", ...BRAND_LINKS.youtube },
  ],
  cta: {
    label: BRAND_LINKS.signUp.label,
    description: "Grátis para começar",
    href: BRAND_LINKS.signUp.href,
  },
  /** Os quatro links da lista editorial, na ordem numerada da direção (01–04). */
  links: [
    {
      key: "demo",
      label: "Ver a Tãmi funcionando",
      description: "Um tour pelo produto, sem criar conta",
      href: DEMO_BASE_HREF,
      external: false,
    },
    /*
     * O "Guia completo da Tãmi" (a playlist do YouTube) saiu por decisão do
     * dono: quem quer ver o produto já tem o tour da `/demo` logo acima, sem
     * criar conta. O canal continua a um toque, no ícone do YouTube do topo.
     */
    {
      key: "pricing",
      label: "Planos e preços",
      description: undefined,
      href: "/#pricing",
      external: false,
    },
    {
      key: "site",
      label: BRAND_LINKS.site.label,
      description: undefined,
      href: BRAND_LINKS.site.href,
      external: false,
    },
  ],
  /** Os slugs são os da `/demo` (`?tela=`), definidos na spec dela. */
  features: [
    { key: "agenda", label: "Agenda" },
    { key: "conteudo", label: "Conteúdo" },
    { key: "campanhas", label: "Campanhas" },
    { key: "financas", label: "Finanças" },
    { key: "whatsapp", label: "WhatsApp" },
  ],
  /**
   * Lido pelo `BioCollabBlock` (Task 9). A direção também grifa "todos" nesse
   * parágrafo — mesmo formato `before/highlight/after` da `tagline`, para o
   * componente não precisar de parsing de marcação no meio do texto.
   */
  collab: {
    title: "Gerencia vários creators?",
    description: {
      before: "No modo Collab, uma conta cuida de ",
      highlight: "todos",
      after: " os creators da casa: agenda, campanhas e finanças de cada um, no mesmo lugar.",
    },
    ctaLabel: "Falar com a gente",
  },
  support: {
    title: "Precisa de ajuda?",
    whatsappLabel: SUPPORT_WHATSAPP_DISPLAY,
    whatsappHref: BRAND_LINKS.supportWhatsApp.href,
    emailLabel: SUPPORT_EMAIL,
    emailHref: BRAND_LINKS.supportEmail.href,
  },
  /**
   * Lido pelo `BioSite` (Task 9), no rodapé da página. Os destinos saem de
   * `legal-documents.ts` — a mesma fonte que o `sitemap.ts` lê — em vez de
   * escritos à mão, para não divergir se o slug mudar.
   */
  footer: {
    terms: { label: "Termos de uso", href: `/${TERMS_OF_USE_DOCUMENT.slug}` },
    privacy: {
      label: "Política de privacidade",
      href: `/${PRIVACY_POLICY_DOCUMENT.slug}`,
    },
  },
} as const;

export const BIO_COPY = defineCopy(BIO_COPY_PT_BR, {
  "en-US": BIO_COPY_EN_US,
  "es-ES": BIO_COPY_ES_ES,
});
